import { useEffect, useState } from 'react';
import { Divider, Stack, DialogContent, Typography, useTheme, Grid, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  FormProvider,
  GlassEffectDialog,
  Iconify,
  RHFAutocomplete,
  RHFTextField,
} from '@app/components';
import { Strings } from '@app/constants';
import { useRequestState } from '@app/hooks';
import { getProjects } from '@app/modules/projects/slice';
import { selectProjectState } from '@app/modules/projects/selector';
import { addTaskBoard, getBackground } from '../slice';
import { selectAddTaskBoardState, selectGetBackgroundState } from '../selector';
import { IAddTaskBoardSchema } from '../types';
import { addTaskBoardSchema, defaultSchema } from '../validators';
import { IMAGES } from './images';

interface Project {
  label: string;
  id: string;
}
interface Props {
  open: boolean;
  onClose: VoidFunction;
  title: string;
  setSelectedColors?: any;
  setBoardTitttle?: any;
  setBoardData?: (data: any[]) => void;
}

const AddBackgroundAndBoard = ({ open, onClose, title, setBoardData }: Props) => {
  const dispatch = useDispatch();

  const methods = useForm<IAddTaskBoardSchema>({
    resolver: yupResolver(title === 'Create Board' ? addTaskBoardSchema : defaultSchema) as any,
  });

  const theme = useTheme();

  const { data: background } = useRequestState({
    stateSelector: selectGetBackgroundState,
    errorShown: true,
  });

  useEffect(() => {
    dispatch(getBackground());
  }, [dispatch]);

  const [selectedColor, setSelectedColor] = useState(Object?.values(IMAGES)[0]);
  const [image, setImage] = useState<File | undefined>();

  useEffect(() => {
    if (background && background.length > 0) {
      setSelectedColor(background[0]?.image);
    }
  }, [background]);
  const [query, setQuery] = useState('');

  const { data } = useRequestState({
    stateSelector: selectProjectState,
  });

  useEffect(() => {
    if (query.length !== 0) {
      dispatch(getProjects(query));
    } else {
      dispatch(getProjects());
    }
  }, [dispatch, query]);

  const handleColorSelect = (color: string) => {
    setImage(undefined);
    setSelectedColor(color);
  };

  const { loading } = useRequestState({
    stateSelector: selectAddTaskBoardState,
    successMessageShown: true,
    onSuccess: onClose,
    errorShown: true,
  });

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];

    if (file) {
      setSelectedColor('');
      methods.setValue('image', file, { shouldValidate: true });
      setImage(file);
    }
  };

  const [showMore, setShowMore] = useState(false);
  const onSubmit = (form: IAddTaskBoardSchema) => {
    const projectId = selectedProject?.id;
    const imageToSubmit = selectedColor || image;

    dispatch(
      addTaskBoard({
        ...form,
        projectId,
        image: imageToSubmit,
        description: form.description?.trim() || '',
      })
    );
  };

  const imagesToShow = showMore ? Object.values(IMAGES) : Object.values(IMAGES)?.slice(0, 6);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const handleProjectSelect = (event: any, value: Project | null) => {
    setSelectedProject(value);
    setQuery('');
  };
  useEffect(() => {
    if (selectedProject) {
      methods.setValue('boardName', `${selectedProject.label} Board`, { shouldValidate: true });
    }
  }, [selectedProject, methods.setValue]);

  const boardNameValue = useWatch({
    control: methods.control,
    name: 'boardName',
  });

  return (
    <>
      <FormProvider methods={methods}>
        <GlassEffectDialog open onClose={onClose} title={title}>
          <Divider sx={{ mt: -3 }} />
          <DialogContent>
            <Stack my={2} justifyContent="center" direction={'row'} spacing={2} ml={6} mr={6}>
              <Stack
                sx={{
                  height: '150px',
                  width: '250px',
                  backgroundImage: image
                    ? `url(${URL.createObjectURL(image)})`
                    : `url(${selectedColor})`,
                  backgroundSize: 'cover',
                  alignItems: 'center',
                }}
              />
            </Stack>
            <Stack>
              <Typography
                sx={{
                  color: theme.palette.taskboard.deepPurple,
                  fontSize: '15px',
                  fontWeight: 700,
                }}
              >
                {Strings.taskBoard.background}
              </Typography>
              <Stack direction={'row'} mt={2}>
                <Stack
                  justifyContent="center"
                  direction={'row'}
                  spacing={1}
                  sx={{ width: '350px' }}
                >
                  <Grid container spacing={1}>
                    {imagesToShow?.map((color: string, index: number) => (
                      <Grid item xs={12} sm={4} md={4} key={index}>
                        <Stack
                          onClick={() => {
                            handleColorSelect(color);
                          }}
                          sx={{
                            height: '60px',
                            width: '100%',
                            backgroundImage: `url(${color})`,
                            backgroundSize: 'cover',
                            borderRadius: '2px',
                            alignItems: 'center',
                            position: 'relative',
                          }}
                        >
                          {selectedColor === color && (
                            <Iconify
                              icon={'mdi:check'}
                              width={30}
                              sx={{
                                color: theme.palette.common.white,
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                              }}
                            />
                          )}
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Stack>
              <Stack
                flexDirection={'row'}
                gap={2}
                alignItems={'center'}
                mt={1}
                justifyContent={'space-between'}
              >
                <Stack>
                  <input type="file" id="file" onChange={handleFileChange} hidden />
                  <label htmlFor="file">
                    <span
                      style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        color: theme.palette.taskboard.raisinBlack,
                        textDecoration: 'underline',
                        fontWeight: 600,
                      }}
                    >
                      {Strings.taskBoard.chooseFromGallery}
                    </span>
                  </label>
                </Stack>
                <Button
                  onClick={() => {
                    setShowMore(!showMore);
                  }}
                  sx={{
                    color: theme.palette.taskboard.raisinBlack,
                    textDecoration: 'underline',
                    '&:hover': {
                      background: 'none',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {showMore ? 'View Less' : 'View More'}
                </Button>
              </Stack>
              {title === 'Create Board' && (
                <Stack mt={2} gap={2}>
                  <Stack>
                    <RHFAutocomplete
                      name={'project'}
                      label={Strings.taskBoard.searchProject}
                      options={(data?.projects || []).map((task: any) => ({
                        label: task.name,
                        id: task.id,
                      }))}
                      getOptionKey={(option: any) => option.id}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      onChange={handleProjectSelect}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: theme.palette.taskboard.raisinBlack,
                          },
                        },
                      }}
                      required={false}
                      InputLabelProps={{
                        sx: {
                          color: theme.palette.taskboard.raisinBlack,
                        },
                      }}
                    />
                  </Stack>
                  <Stack>
                    <RHFTextField
                      name="boardName"
                      label="Board Name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: theme.palette.taskboard.raisinBlack,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.taskboard.deepPurple,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: theme.palette.taskboard.raisinBlack,
                          '&.Mui-focused': {
                            color: theme.palette.taskboard.deepPurple,
                          },
                          '&.MuiInputLabel-shrink': {
                            background: theme.palette.background.paper,
                            padding: '0 4px',
                            zIndex: 1,
                          },
                        },
                      }}
                      InputLabelProps={{
                        shrink: Boolean(boardNameValue) || undefined,
                        style: { color: theme.palette.taskboard.raisinBlack },
                      }}
                    />
                  </Stack>
                  <Stack>
                    <RHFTextField
                      multiline
                      minRows={2}
                      name="description"
                      label="Description"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: theme.palette.taskboard.raisinBlack,
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: { color: theme.palette.taskboard.raisinBlack },
                      }}
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <Divider sx={{ pt: 2 }} />
          <Stack width={'95%'} justifyContent={'center'} alignItems={'end'} mt={1}>
            <LoadingButton
              loading={loading}
              variant="contained"
              color="primary"
              type="submit"
              onClick={methods.handleSubmit(onSubmit)}
              sx={{
                bgcolor: theme.palette.taskboard.raisinBlack,
                '&:hover': {
                  bgcolor: theme.palette.taskboard.raisinBlack,
                },
              }}
            >
              {Strings.button.save}
            </LoadingButton>
          </Stack>
        </GlassEffectDialog>
      </FormProvider>
    </>
  );
};

export default AddBackgroundAndBoard;
